import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, InputNumber, Modal, Switch, Table, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { StatusMark } from '../../../components/StatusMark';
import {
  DICT_STATUS_LABEL,
  type DictGroup,
  type DictItem,
  type DictStatus
} from './dictManageData';

const DRAFT_ID = '__dict_item_draft__';

type DraftItem = {
  id: string;
  label: string;
  value: string;
  sort: number;
  status: DictStatus;
  remark: string;
  createdAt: string;
  version?: number;
  isDraft?: boolean;
};

type DictDetailModalProps = {
  open: boolean;
  group: DictGroup | null;
  itemsLoading?: boolean;
  onClose: () => void;
  onChangeItems: (groupId: string, items: DictItem[]) => void;
  onCreateItem: (item: Pick<DictItem, 'label' | 'value' | 'sort' | 'status' | 'remark'>) => Promise<boolean>;
  onDeleteItem: (item: DictItem) => Promise<boolean>;
  onUpdateItem: (item: DictItem) => Promise<boolean>;
};

const emptyDraft = (sort: number): DraftItem => ({
  id: DRAFT_ID,
  label: '',
  value: '',
  sort,
  status: 'normal',
  remark: '',
  createdAt: '',
  isDraft: true
});

export function DictDetailModal({
  open,
  group,
  itemsLoading = false,
  onClose,
  onChangeItems,
  onCreateItem,
  onDeleteItem,
  onUpdateItem
}: DictDetailModalProps) {
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState<DraftItem>(emptyDraft(0));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftItem | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setDrafting(false);
      setEditingId(null);
      setEditDraft(null);
    }
  }, [open]);

  const items = useMemo(
    () => [...(group?.items ?? [])].sort((a, b) => a.sort - b.sort),
    [group]
  );

  const dataSource: DraftItem[] = useMemo(() => {
    const rows: DraftItem[] = items.map((i) => ({ ...i }));
    if (drafting) {
      return [draft, ...rows];
    }
    return rows;
  }, [items, drafting, draft]);

  const startCreate = () => {
    if (drafting || editingId) {
      message.warning('请先保存或取消当前编辑行');
      return;
    }
    const nextSort = items.length ? Math.max(...items.map((i) => i.sort)) + 1 : 0;
    setDraft(emptyDraft(nextSort));
    setDrafting(true);
  };

  const cancelCreate = () => {
    setDrafting(false);
    setDraft(emptyDraft(0));
  };

  const saveCreate = async () => {
    if (!group || saving) return;
    const label = draft.label.trim();
    const value = draft.value.trim();
    const remark = draft.remark.trim();
    if (!label) {
      message.warning('请输入字典标签');
      return;
    }
    if (label.length > 200) {
      message.warning('字典标签最多 200 个字符');
      return;
    }
    if (!value) {
      message.warning('请输入字典键值');
      return;
    }
    if (value.length > 128) {
      message.warning('字典键值最多 128 个字符');
      return;
    }
    if (remark.length > 500) {
      message.warning('说明最多 500 个字符');
      return;
    }
    if (group.items.some((item) => item.value === value)) {
      message.error('字典键值已存在');
      return;
    }
    setSaving(true);
    try {
      const ok = await onCreateItem({
        label,
        value,
        sort: draft.sort,
        status: draft.status,
        remark
      });
      if (ok) cancelCreate();
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item: DictItem) => {
    if (drafting || editingId) {
      message.warning('请先保存或取消当前编辑行');
      return;
    }
    setEditingId(item.id);
    setEditDraft({ ...item });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const saveEdit = async () => {
    if (!group || !editDraft || !editingId || saving) return;
    const current = group.items.find((item) => item.id === editingId);
    const label = editDraft.label.trim();
    const value = (current?.value ?? editDraft.value).trim();
    const remark = editDraft.remark.trim();
    if (!label) {
      message.warning('请输入字典标签');
      return;
    }
    if (label.length > 200) {
      message.warning('字典标签最多 200 个字符');
      return;
    }
    if (!value) {
      message.warning('请输入字典键值');
      return;
    }
    if (value.length > 128) {
      message.warning('字典键值最多 128 个字符');
      return;
    }
    if (remark.length > 500) {
      message.warning('说明最多 500 个字符');
      return;
    }
    setSaving(true);
    try {
      const ok = await onUpdateItem({
        ...editDraft,
        id: editingId,
        label,
        value,
        sort: editDraft.sort,
        status: editDraft.status,
        remark,
        version: current?.version ?? editDraft.version
      });
      if (ok) cancelEdit();
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (item: DictItem, checked: boolean) => {
    if (!group || saving) return;
    if (drafting || editingId) {
      message.warning('请先保存或取消当前编辑行');
      return;
    }
    setSaving(true);
    try {
      await onUpdateItem({
        ...item,
        status: checked ? 'normal' : 'disabled'
      });
    } finally {
      setSaving(false);
    }
  };

  const removeItem = (item: DictItem) => {
    if (!group) return;
    if (drafting || editingId) {
      message.warning('请先保存或取消当前编辑行');
      return;
    }
    if (item.version == null || item.version < 0) {
      message.error('缺少版本号，无法删除');
      return;
    }
    Modal.confirm({
      title: '确认删除',
      content: `确定删除字典项「${item.label}」吗？`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      centered: true,
      onOk: async () => {
        const ok = await onDeleteItem(item);
        if (!ok) return Promise.reject(new Error('delete failed'));
      }
    });
  };

  const patchDraft = (patch: Partial<DraftItem>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const patchEdit = (patch: Partial<DraftItem>) => {
    setEditDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const columns: ColumnsType<DraftItem> = [
    {
      title: '编号',
      width: 64,
      render: (_, row, index) => (row.isDraft ? '新' : drafting ? index : index + 1)
    },
    {
      title: '字典标签',
      dataIndex: 'label',
      render: (_, row) => {
        if (row.isDraft) {
          return (
            <Input
              size='small'
              maxLength={200}
              placeholder='如：男'
              value={draft.label}
              onChange={(e) => patchDraft({ label: e.target.value })}
            />
          );
        }
        if (editingId === row.id && editDraft) {
          return (
            <Input
              size='small'
              maxLength={200}
              value={editDraft.label}
              onChange={(e) => patchEdit({ label: e.target.value })}
            />
          );
        }
        return <span className='admin-cell-primary'>{row.label}</span>;
      }
    },
    {
      title: '字典键值',
      dataIndex: 'value',
      render: (_, row) => {
        if (row.isDraft) {
          return (
            <Input
              size='small'
              maxLength={128}
              placeholder='如：0'
              value={draft.value}
              onChange={(e) => patchDraft({ value: e.target.value })}
            />
          );
        }
        if (editingId === row.id && editDraft) {
          return <Input size='small' maxLength={128} value={editDraft.value} disabled />;
        }
        return <code className='admin-dict-code'>{row.value}</code>;
      }
    },
    {
      title: '排序',
      dataIndex: 'sort',
      width: 88,
      render: (_, row) => {
        if (row.isDraft) {
          return (
            <InputNumber
              size='small'
              min={0}
              precision={0}
              style={{ width: '100%' }}
              value={draft.sort}
              onChange={(v) => patchDraft({ sort: Number(v ?? 0) })}
            />
          );
        }
        if (editingId === row.id && editDraft) {
          return (
            <InputNumber
              size='small'
              min={0}
              precision={0}
              style={{ width: '100%' }}
              value={editDraft.sort}
              onChange={(v) => patchEdit({ sort: Number(v ?? 0) })}
            />
          );
        }
        return row.sort;
      }
    },
    {
      title: '启用',
      dataIndex: 'status',
      width: 72,
      align: 'center',
      render: (_, row) => {
        if (row.isDraft) {
          return (
            <Switch
              size='small'
              checked={draft.status === 'normal'}
              onChange={(checked) => patchDraft({ status: checked ? 'normal' : 'disabled' })}
            />
          );
        }
        if (editingId === row.id && editDraft) {
          return (
            <Switch
              size='small'
              checked={editDraft.status === 'normal'}
              onChange={(checked) => patchEdit({ status: checked ? 'normal' : 'disabled' })}
            />
          );
        }
        return (
          <Switch
            size='small'
            checked={row.status === 'normal'}
            onChange={(checked) => toggleStatus(row, checked)}
          />
        );
      }
    },
    {
      title: '备注',
      dataIndex: 'remark',
      ellipsis: true,
      render: (_, row) => {
        if (row.isDraft) {
          return (
            <Input
              size='small'
              maxLength={500}
              placeholder='备注'
              value={draft.remark}
              onChange={(e) => patchDraft({ remark: e.target.value })}
            />
          );
        }
        if (editingId === row.id && editDraft) {
          return (
            <Input
              size='small'
              maxLength={500}
              value={editDraft.remark}
              onChange={(e) => patchEdit({ remark: e.target.value })}
            />
          );
        }
        return row.remark || '—';
      }
    },
    {
      title: '版本',
      dataIndex: 'version',
      width: 90,
      render: (version: number | undefined, row) => (row.isDraft || version == null ? '—' : version)
    },
    {
      title: '操作',
      key: 'actions',
      width: 110,
      render: (_, row) => {
        if (row.isDraft) {
          return (
            <div className='admin-row-actions'>
              <button type='button' className='admin-link-btn' onClick={saveCreate}>
                保存
              </button>
              <button type='button' className='admin-link-btn admin-link-btn--muted' onClick={cancelCreate}>
                取消
              </button>
            </div>
          );
        }
        if (editingId === row.id) {
          return (
            <div className='admin-row-actions'>
              <button type='button' className='admin-link-btn' onClick={saveEdit}>
                保存
              </button>
              <button type='button' className='admin-link-btn admin-link-btn--muted' onClick={cancelEdit}>
                取消
              </button>
            </div>
          );
        }
        return (
          <div className='admin-row-actions'>
            <button type='button' className='admin-link-btn' onClick={() => startEdit(row)}>
              编辑
            </button>
            <button
              type='button'
              className='admin-link-btn admin-link-btn--danger'
              onClick={() => removeItem(row)}
            >
              删除
            </button>
          </div>
        );
      }
    }
  ];

  return (
    <Modal
      title={group ? `字典详情 — ${group.name}` : '字典详情'}
      open={open}
      onCancel={() => {
        if (drafting || editingId) {
          message.warning('请先保存或取消当前编辑行');
          return;
        }
        onClose();
      }}
      destroyOnClose
      centered
      width={1100}
      footer={null}
      className='admin-dict-detail-modal'
    >
      {group ? (
        <div className='admin-dict-detail'>
          <div className='admin-dict-detail-toolbar'>
            <div className='admin-dict-detail-meta'>
              <span>
                类型：<code className='admin-dict-code'>{group.type}</code>
              </span>
              <span>字典项 {items.length} 个</span>
              <StatusMark tone={group.status === 'normal' ? 'success' : 'muted'}>
                {DICT_STATUS_LABEL[group.status]}
              </StatusMark>
            </div>
            <Button
              type='primary'
              icon={<PlusOutlined />}
              className='wb-btn-primary'
              disabled={drafting || !!editingId}
              onClick={startCreate}
            >
              新增
            </Button>
          </div>
          <Table<DraftItem>
            rowKey='id'
            columns={columns}
            dataSource={dataSource}
            loading={itemsLoading}
            pagination={false}
            size='middle'
            tableLayout='fixed'
            className='admin-user-table admin-dict-detail-table'
            locale={{ emptyText: '暂无字典项，点击「新增」在表格中添加一行' }}
          />
        </div>
      ) : null}
    </Modal>
  );
}
